import { MaskitoPipe } from '@maskito/angular';
import {
  maskitoInitialCalibrationPlugin,
  MaskitoMaskExpression,
  MaskitoOptions,
  maskitoUpdateElement
} from '@maskito/core';
import { ElementState } from '@maskito/core/src/lib/types';
import { maskitoCaretGuard, maskitoEventHandler, maskitoNumber } from '@maskito/kit';

import { includes, isFunction, isNaN, replace } from 'lodash-es';

export const amountPrefix = '$';
export const ratePostfix = '%';
export const rateAmountSymbolRegExPattern = new RegExp(`[${[amountPrefix, ratePostfix, '\\,'].join('|')}]`, 'g');

export const cleanUpNullishMaskValue = (value: string | null, pattern: string | RegExp = '') => {
  const result = value ? parseFloat(replace(replace(value, pattern, ''), /[^\d.]/g, '')) : null;
  return !isNaN(result) ? result : null;
};

export const cleanUpMaskValue = (value: string | null, pattern: string | RegExp = '') => {
  const result = value ? parseFloat(replace(replace(value, pattern, ''), /[^\d.]/g, '')) : 0;
  return !isNaN(result) ? result : 0;
};

const rateMaskOptions = maskitoNumber({
  max: 99,
  maximumFractionDigits: 3,
  min: 0,
  postfix: ratePostfix
});

export const rateMask: MaskitoOptions = {
  ...rateMaskOptions,
  plugins: [
    ...rateMaskOptions.plugins,
    maskitoCaretGuard((value) => [0, value.length - ratePostfix.length]),
    maskitoEventHandler('blur', (element) => {
      if (element.value === ratePostfix) {
        maskitoUpdateElement(element, '');
      }
    }),
    maskitoInitialCalibrationPlugin({
      ...rateMaskOptions
    })
  ]
};

const amountMaskOptions = maskitoNumber({
  maximumFractionDigits: 3,
  prefix: amountPrefix,
  thousandSeparator: ','
});

export const amountMask: MaskitoOptions = {
  ...amountMaskOptions,
  plugins: [
    ...amountMaskOptions.plugins,
    maskitoCaretGuard((value) => [amountPrefix.length, value.length]),
    maskitoEventHandler('blur', (element) => {
      if (element.value === amountPrefix) {
        maskitoUpdateElement(element, '');
      }
    }),
    maskitoInitialCalibrationPlugin({
      ...amountMaskOptions
    })
  ]
};

const surfaceMaskOptions = maskitoNumber({
  maximumFractionDigits: 3,
  min: 0,
  thousandSeparator: ','
});

export const surfaceMask: MaskitoOptions = {
  ...surfaceMaskOptions,
  plugins: [
    ...surfaceMaskOptions.plugins,
    maskitoCaretGuard((value) => [0, value.length]),
    maskitoInitialCalibrationPlugin({
      ...surfaceMaskOptions
    })
  ]
};

const getRateAmountMask = (elementState: ElementState, maxValue: number) => {
  const formattedValue = Number(replace(elementState.value, rateAmountSymbolRegExPattern, ''));

  let result: MaskitoMaskExpression = isFunction(amountMask.mask) ? amountMask.mask(elementState) : amountMask.mask;
  if (formattedValue <= maxValue) {
    result = isFunction(rateMask.mask) ? rateMask.mask(elementState) : rateMask.mask;
  }

  return result;
};

export function rateAmountMask(maxValue = 99): MaskitoOptions {
  return {
    mask: (elementState: ElementState): MaskitoMaskExpression => getRateAmountMask(elementState, maxValue),
    plugins: [
      maskitoCaretGuard((value) => {
        const hasDot = includes(['.'], value);
        const formattedValue = Number(replace(value, rateAmountSymbolRegExPattern, ''));
        return !hasDot
          ? !isNaN(formattedValue) && formattedValue <= maxValue
            ? [0, value.length - ratePostfix.length]
            : [amountPrefix.length, value.length]
          : [0, value.length];
      }),
      maskitoInitialCalibrationPlugin({
        mask: (elementState: ElementState): MaskitoMaskExpression => getRateAmountMask(elementState, maxValue)
      })
    ],
    postprocessors: [
      (elementState: ElementState, initialElementState: ElementState) => {
        const formattedInitialValue = Number(replace(initialElementState.value, rateAmountSymbolRegExPattern, ''));
        const sanitizedValue = replace(elementState.value, rateAmountSymbolRegExPattern, '');
        const formattedValue = Number(sanitizedValue);
        const isRate = !isNaN(formattedValue) && formattedValue <= maxValue;

        let newSelection = elementState.selection;

        if (formattedInitialValue <= maxValue && formattedValue > maxValue) {
          if (initialElementState.selection[1] < elementState.selection[1]) {
            newSelection = [
              elementState.value.length + amountPrefix.length,
              elementState.value.length + amountPrefix.length
            ];
          } else if (initialElementState.selection[1] > elementState.selection[1]) {
            newSelection = [elementState.value.length, elementState.value.length];
          }
        } else if (!isRate) {
          const oldCommaCount = (initialElementState.value.match(/,/g) || []).length;
          const newValue = new MaskitoPipe().transform(sanitizedValue, amountMask);
          const newCommaCount = (newValue.match(/,/g) || []).length;

          if (newCommaCount > oldCommaCount) {
            newSelection = [newSelection[0] + 1, newSelection[1] + 1];
          }
        }

        return {
          selection: newSelection,
          value: isRate
            ? new MaskitoPipe().transform(sanitizedValue, rateMask)
            : new MaskitoPipe().transform(sanitizedValue, amountMask)
        };
      }
    ]
  };
}

const getPhoneMask = () => [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

export const phoneMask: MaskitoOptions = {
  mask: getPhoneMask(),
  overwriteMode: (elementState: ElementState) => (elementState.value.length === 10 ? 'replace' : 'shift'),
  plugins: [
    maskitoInitialCalibrationPlugin({
      mask: getPhoneMask()
    })
  ]
};
